import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Search, TrendingUp, TrendingDown, Trophy, Dumbbell, Film, Zap, CalendarPlus, Save, Target } from "lucide-react";
import GateProgress from "@/components/GateProgress";
import SelfReportInsights from "@/components/SelfReportInsights";

const SRC_ICON = { tournament: Trophy, league: Zap, drill: Dumbbell, film: Film, rec: Zap };
const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : "");

export default function PlayersLab() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("all");
  const [aq, setAq] = useState("");
  const [events, setEvents] = useState([]);
  const [goal, setGoal] = useState("");
  const [plan, setPlan] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/player/lab").then(({ data }) => {
      setD(data); setGoal(data.season_plan?.season_goal || ""); setPlan(data.season_plan?.items || []);
    }).catch(() => toast.error("Couldn't load your Lab"));
    api.get("/public/events").then(({ data }) => setEvents((data || []).filter((t) => t.status !== "complete" && t.status !== "completed"))).catch(() => {});
  }, []);

  const ledger = useMemo(() => (d?.ledger || []).filter((e) =>
    (src === "all" || e.source === src) &&
    (!q || `${e.where} ${e.detail} ${e.source}`.toLowerCase().includes(q.toLowerCase()))), [d, q, src]);
  const activity = useMemo(() => (d?.activity || []).filter((a) =>
    !aq || `${a.title} ${a.detail} ${a.kind}`.toLowerCase().includes(aq.toLowerCase())), [d, aq]);

  if (!d) return null;
  const inPlan = (id) => plan.some((p) => p.id === id);
  const toggle = (t) => setPlan((p) => inPlan(t.id) ? p.filter((x) => x.id !== t.id)
    : [...p, { id: t.id, name: t.name, date: t.date, level: t.pbr_level }]);
  const save = async () => {
    setBusy(true);
    try { await api.post("/player/lab/season", { items: plan, season_goal: goal }); toast.success("Season plan saved — go build your legacy"); }
    catch { toast.error("Save failed"); }
    setBusy(false);
  };
  const overall = d.attributes.length ? Math.round(d.attributes.reduce((s, a) => s + a.rating, 0) / d.attributes.length) : null;

  return (
    <div className="space-y-8 pb-16" data-testid="players-lab-page">
      <div className="relative border border-cyan-400/40 bg-cyan-400/[0.04] p-6 overflow-hidden">
        <FlaskConical className="absolute -right-4 -top-4 w-40 h-40 text-cyan-400/10 animate-pulse" />
        <p className="font-mono text-cyan-300 uppercase tracking-[0.35em] text-xs mb-2">🧪 The Player's Lab</p>
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl leading-none mb-2">{d.name}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="text-cyan-300 font-black text-2xl font-display">PSI {d.psi}</span>
          <span className="text-zinc-400 self-end">{d.division}{overall != null ? ` · Drill OVR ${overall}` : ""} · {d.stats.pbr_points} PBR pts</span>
          <span className="self-end">🥇{d.stats.medals.gold} 🥈{d.stats.medals.silver} 🥉{d.stats.medals.bronze}</span>
        </div>
      </div>

      {/* Create-A-Player attributes */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="lab-attributes">
        <h2 className="font-display font-bold uppercase text-lg mb-4 flex items-center gap-2"><Target className="w-4 h-4 text-cyan-400" /> Your Build — Attributes</h2>
        {d.attributes.length === 0 && <p className="text-sm text-zinc-500">Run drills in the Training Hub to start building your attribute ratings.</p>}
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {d.attributes.map((a) => (
            <div key={a.name} data-testid={`lab-attr-${a.name}`}>
              <div className="flex justify-between text-[10px] uppercase tracking-widest mb-1">
                <span className={a.name.toLowerCase() === (d.weakness || "") ? "text-amber-400 font-bold" : "text-zinc-400"}>{a.name}{a.name.toLowerCase() === (d.weakness || "") ? " · focus" : ""}</span>
                <span className="font-mono text-cyan-300 font-bold">{a.rating}</span>
              </div>
              <div className="h-2 bg-pb-elevated border border-pb-border/50"><div className="h-full transition-all duration-700" style={{ width: `${a.rating}%`, background: a.rating >= 75 ? "#4ADE80" : a.rating >= 50 ? "#22D3EE" : "#F59E0B" }} /></div>
            </div>
          ))}
        </div>
        {(d.recommendations || []).length > 0 && (
          <div className="mt-4 border-t border-pb-border pt-3 space-y-1" data-testid="lab-recs">
            {d.recommendations.map((r, i) => (
              <button key={i} onClick={() => nav(r.type === "tip" ? "/lab" : "/training")} className="block text-left text-xs text-cyan-300 hover:text-white transition-colors">{r.type === "tip" ? "💡" : "⚗️"} {r.text}{r.type === "tip" ? "" : " →"}</button>
            ))}
          </div>
        )}
      </div>

      <SelfReportInsights />

      <GateProgress />

      {/* Season Builder */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="lab-season-builder">
        <h2 className="font-display font-bold uppercase text-lg mb-1 flex items-center gap-2"><CalendarPlus className="w-4 h-4 text-cyan-400" /> Season Builder</h2>
        <p className="text-xs text-zinc-500 mb-3">Design your tournament year — pick your targets and chase the gates.</p>
        <input data-testid="lab-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Season goal — e.g. Qualify for Nationals, hit PSI 80…"
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-400 mb-3" />
        <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1 mb-3">
          {events.map((t) => (
            <button key={t.id} data-testid={`lab-plan-${t.id}`} onClick={() => toggle(t)}
              className={`flex items-center justify-between gap-2 border px-3 py-2 text-xs text-left transition-colors ${inPlan(t.id) ? "border-cyan-400 bg-cyan-400/10 text-white" : "border-pb-border text-zinc-400 hover:border-cyan-400/50"}`}>
              <span className="truncate">{t.name} <span className="text-zinc-600">· {t.date}</span></span>
              <span className="shrink-0 font-black">{inPlan(t.id) ? "✓" : "+"}</span>
            </button>
          ))}
          {events.length === 0 && <p className="text-xs text-zinc-600">No upcoming events posted yet.</p>}
        </div>
        {plan.length > 0 && <p className="text-[11px] text-cyan-300 mb-3" data-testid="lab-plan-count">{plan.length} event{plan.length > 1 ? "s" : ""} on your season path</p>}
        <button data-testid="lab-save-season" disabled={busy} onClick={save}
          className="flex items-center gap-2 bg-cyan-400 text-black font-black uppercase tracking-widest text-xs px-5 py-2.5 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50">
          <Save className="w-4 h-4" /> Save Season Plan
        </button>
      </div>

      {/* PSI Ledger */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="lab-ledger">
        <h2 className="font-display font-bold uppercase text-lg mb-3">PSI Movement Ledger</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="lab-ledger-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your PSI history…"
              className="w-full bg-pb-elevated border border-pb-border pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-400" />
          </div>
          {["all", "tournament", "league", "rec", "film", "drill"].map((s) => (
            <button key={s} data-testid={`lab-src-${s}`} onClick={() => setSrc(s)}
              className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-2 border transition-colors ${src === s ? "border-cyan-400 text-cyan-300 bg-cyan-400/10" : "border-pb-border text-zinc-500"}`}>{s}</button>
          ))}
        </div>
        <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
          {ledger.map((e, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-pb-border/50 py-1.5 text-xs" data-testid={`ledger-row-${i}`}>
              <span className="text-zinc-600 font-mono w-20 shrink-0">{fmtD(e.date)}</span>
              {e.delta != null && (
                <span className={`flex items-center gap-1 font-black w-14 shrink-0 ${e.delta >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {e.delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{e.delta >= 0 ? "+" : ""}{e.delta}
                </span>
              )}
              <span className="text-[9px] uppercase tracking-widest text-cyan-400/80 w-20 shrink-0">{e.source}</span>
              <span className="text-zinc-300">{e.where}{e.detail ? ` — ${e.detail}` : ""}</span>
              <span className="ml-auto font-mono text-zinc-500">{e.score != null ? `match score ${e.score}` : ""}</span>
            </div>
          ))}
          {ledger.length === 0 && <p className="text-xs text-zinc-600 py-3">No PSI events match — play matches, log drills or upload film to move the needle.</p>}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="lab-activity">
        <h2 className="font-display font-bold uppercase text-lg mb-3">Everything You've Done</h2>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="lab-activity-search" value={aq} onChange={(e) => setAq(e.target.value)} placeholder="Search tournaments, leagues, drills, film…"
            className="w-full bg-pb-elevated border border-pb-border pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-400" />
        </div>
        <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
          {activity.map((a, i) => {
            const Icon = SRC_ICON[a.kind] || Zap;
            return (
              <div key={i} className="flex flex-wrap items-center gap-x-3 border-t border-pb-border/50 py-2 text-xs" data-testid={`activity-row-${i}`}>
                <span className="text-zinc-600 font-mono w-20 shrink-0">{a.date}</span>
                <Icon className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                <span className="font-semibold text-white">{a.title}</span>
                {a.medal && <span>{{ gold: "🥇", silver: "🥈", bronze: "🥉" }[a.medal]}</span>}
                <span className="text-zinc-500">{a.detail}</span>
              </div>
            );
          })}
          {activity.length === 0 && <p className="text-xs text-zinc-600 py-3">Your story starts with your first event.</p>}
        </div>
      </div>
    </div>
  );
}
